// Door lock tile with an inline PIN popover (no side panel). Clicking the tile
// opens a small "Enter PIN" patch ABOVE it; a correct PIN (validated against the
// logged-in user's password, like DoorScreen) publishes OPENDOOR.

import React, { useState } from 'react';
import {
  Card, CardActionArea, Box, Typography, Popover, TextField, Button, Alert,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { useApp } from '../state/AppContext';
import { publishCommand } from '../utils/deviceController';
import DeviceStatusDot from './DeviceStatusDot';

export default function DoorControlCard({ device, subtitle }) {
  const { userId, user } = useApp();
  const uno = device.UniqueNumber;
  const [anchor, setAnchor] = useState(null);
  const [pin, setPin] = useState('');
  const [msg, setMsg] = useState(null);

  const openPopover = (e) => { setPin(''); setMsg(null); setAnchor(e.currentTarget); };
  const closePopover = () => setAnchor(null);

  const unlock = () => {
    if (pin === String(user?.Password ?? '')) {
      publishCommand(uno, 'OPENDOOR', userId);
      setMsg({ type: 'success', text: 'Door unlock sent.' });
      setPin('');
    } else {
      setMsg({ type: 'error', text: 'Incorrect PIN.' });
      setPin('');
    }
  };

  const name = device.DeviceName || uno;

  return (
    <>
      <Card sx={{ bgcolor: 'primary.main', borderRadius: 3, height: '100%' }}>
        <CardActionArea onClick={openPopover} sx={{ p: 2, height: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, color: 'primary.contrastText' }}>
            {subtitle && (
              <Typography variant="caption" noWrap sx={{ opacity: 0.75, maxWidth: '100%' }}>{subtitle}</Typography>
            )}
            <LockIcon fontSize="large" />
            <Typography variant="body1" fontWeight={600} noWrap sx={{ maxWidth: '100%' }}>{name}</Typography>
            <DeviceStatusDot device={device} />
          </Box>
        </CardActionArea>
      </Card>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, width: 220, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockOpenIcon fontSize="small" color="inherit" />
            <Typography variant="subtitle2" color="text.primary">Enter PIN to open</Typography>
          </Box>
          {msg && <Alert severity={msg.type} sx={{ py: 0 }}>{msg.text}</Alert>}
          <TextField
            type="password" value={pin} label="PIN" size="small" autoFocus fullWidth
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => e.key === 'Enter' && unlock()}
            inputProps={{ inputMode: 'numeric', maxLength: 4 }}
          />
          <Button variant="contained" fullWidth startIcon={<LockOpenIcon />} onClick={unlock}>
            Unlock
          </Button>
        </Box>
      </Popover>
    </>
  );
}
